import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  form,
  FormField,
  maxLength,
  minLength,
  required,
} from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import {
  SignalButton,
  SignalFormDirective,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import type {
  CreateCompanyApiResponse,
  CreateCompanyRequest,
} from '@core/api/workspace/models';
import {
  CreateCompanyRequestBillingCycle,
  CreateCompanyRequestPlanKey,
} from '@core/api/workspace/models';
import type { CreateCompanyRequestBillingCycle as BillingCycle } from '@core/api/workspace/models/createCompanyRequestBillingCycle';
import type { CreateCompanyRequestPlanKey as PlanKey } from '@core/api/workspace/models/createCompanyRequestPlanKey';

const VALID_PLAN_KEYS = Object.values(CreateCompanyRequestPlanKey);
const VALID_BILLING_CYCLES = Object.values(CreateCompanyRequestBillingCycle);

interface CreateCompanyFormModel {
  companyName: string;
  workspaceName: string;
}

@Component({
  selector: 'app-create-company-page',
  imports: [FormField, SignalFormDirective, RouterLink, SignalTextField, SignalButton],
  templateUrl: './create-company-page.html',
  styleUrl: './create-company-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateCompanyPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly tokenStorage = inject(TokenStorageService);

  readonly createState = new State<CreateCompanyApiResponse>();
  readonly currentYear = new Date().getFullYear();

  readonly selectedPlanKey = signal<PlanKey>(CreateCompanyRequestPlanKey.free);
  readonly selectedBillingCycle = signal<BillingCycle>(
    CreateCompanyRequestBillingCycle.MONTHLY,
  );

  readonly companyModel = signal<CreateCompanyFormModel>({
    companyName: '',
    workspaceName: 'Default Workspace',
  });

  readonly companyForm = form(this.companyModel, (schemaPath) => {
    required(schemaPath.companyName, { message: 'This field is required' });
    minLength(schemaPath.companyName, 2, { message: 'Minimum 2 characters required' });
    maxLength(schemaPath.companyName, 100, { message: 'Maximum 100 characters allowed' });

    required(schemaPath.workspaceName, { message: 'This field is required' });
    minLength(schemaPath.workspaceName, 2, { message: 'Minimum 2 characters required' });
    maxLength(schemaPath.workspaceName, 100, { message: 'Maximum 100 characters allowed' });
  });

  readonly canSubmit = computed(() => {
    if (this.createState.loading()) {
      return false;
    }

    const { companyName, workspaceName } = this.companyModel();
    return companyName.trim().length >= 2 && workspaceName.trim().length >= 2;
  });

  ngOnInit(): void {
    if (!this.tokenStorage.isAuthenticated()) {
      this.router.navigate(['/signup']);
      return;
    }

    this.applyQueryParams();
  }

  private applyQueryParams(): void {
    const planKeyParam = this.route.snapshot.queryParamMap
      .get('planKey')
      ?.trim()
      .toLowerCase();
    const billingCycleParam = this.route.snapshot.queryParamMap
      .get('billingCycle')
      ?.trim()
      .toUpperCase();
    const companyNameParam = this.route.snapshot.queryParamMap.get('companyName');

    const planKey = VALID_PLAN_KEYS.includes(planKeyParam as PlanKey)
      ? (planKeyParam as PlanKey)
      : CreateCompanyRequestPlanKey.free;

    const billingCycle = VALID_BILLING_CYCLES.includes(billingCycleParam as BillingCycle)
      ? (billingCycleParam as BillingCycle)
      : CreateCompanyRequestBillingCycle.MONTHLY;

    this.selectedPlanKey.set(planKey);
    this.selectedBillingCycle.set(billingCycle);

    if (companyNameParam) {
      this.companyModel.update((model) => ({
        ...model,
        companyName: companyNameParam,
      }));
    }
  }

  onSubmit(): void {
    if (!this.canSubmit() || !validateSignalForm(this.companyForm)) {
      return;
    }

    const payload: CreateCompanyRequest = {
      companyName: this.companyModel().companyName.trim(),
      workspaceName: this.companyModel().workspaceName.trim(),
      planKey: this.selectedPlanKey(),
      billingCycle: this.selectedBillingCycle(),
    };

    this.executeRequest({
      state: this.createState,
      request: this.userAPIService.createCompany(payload),
      handleError: true,
      successMessage: 'Company created!',
      onSuccess: (response) => {
        this.tokenStorage.updateWorkspaceSession(response.data);
        this.router.navigate(['/onboarding']);
      },
    });
  }
}
